import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Globe, Menu, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_1b05ad9b-00ea-4f44-a8ab-b9119f3f327f/artifacts/eb517133_Design%20ohne%20Titel%20%2811%29.png";

const Header = () => {
  const { t, lang, setLang, languages } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [openLang, setOpenLang] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "values", label: t.nav.values },
    { id: "contact", label: t.nav.contact },
  ];

  const goTo = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-[rgba(31,107,122,0.18)] shadow-[0_4px_20px_-12px_rgba(31,107,122,0.3)]"
          : "border-b border-[rgba(31,107,122,0.08)]"
      }`}
      style={{
        background: scrolled
          ? "rgba(225, 238, 240, 0.92)"
          : "rgba(225, 238, 240, 0.78)",
        backdropFilter: "saturate(160%) blur(14px)",
        WebkitBackdropFilter: "saturate(160%) blur(14px)",
      }}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-1 md:px-8 md:py-1">
        {/* Logo */}
        <button
          type="button"
          onClick={() => goTo("hero")}
          className="flex items-center"
          data-testid="header-logo"
        >
          <img
            src={LOGO_URL}
            alt="Kompass Heidelberg"
            className="object-contain"
            style={{
              mixBlendMode: "multiply",
              height: "clamp(110px, 16vw, 220px)",
              width: "auto",
            }}
          />
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => goTo(n.id)}
              data-testid={`nav-${n.id}`}
              className="text-sm font-medium text-[color:var(--kompass-ink-soft)] hover:text-[color:var(--kompass-primary)] transition-colors"
            >
              {n.label}
            </button>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Language switcher */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setOpenLang((v) => !v)}
              data-testid="lang-switcher"
              className="flex items-center gap-2 rounded-full border border-[rgba(31,107,122,0.15)] bg-white/70 px-3 py-2 text-sm font-medium text-[color:var(--kompass-ink-soft)] hover:border-[rgba(31,107,122,0.4)] transition-colors"
            >
              <Globe className="h-4 w-4" strokeWidth={1.6} />
              <span className="hidden sm:inline">
                {languages.find((l) => l.code === lang)?.native}
              </span>
              <span className="sm:hidden uppercase">{lang}</span>
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform ${openLang ? "rotate-180" : ""}`}
                strokeWidth={2}
              />
            </button>

            <AnimatePresence>
              {openLang && (
                <>
                  <button
                    type="button"
                    aria-label="close"
                    className="fixed inset-0 z-40 cursor-default"
                    onClick={() => setOpenLang(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: -6, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.97 }}
                    transition={{ duration: 0.18 }}
                    className="absolute end-0 mt-2 w-44 z-50 overflow-hidden rounded-2xl border border-[rgba(31,107,122,0.12)] bg-white shadow-[0_20px_60px_-20px_rgba(31,107,122,0.35)]"
                  >
                    {languages.map((l) => {
                      const active = l.code === lang;
                      return (
                        <button
                          key={l.code}
                          type="button"
                          data-testid={`lang-option-${l.code}`}
                          onClick={() => {
                            setLang(l.code);
                            setOpenLang(false);
                          }}
                          className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-sm transition-colors ${
                            active
                              ? "bg-[rgba(31,107,122,0.07)] text-[color:var(--kompass-primary)] font-semibold"
                              : "hover:bg-[rgba(31,107,122,0.05)] text-[color:var(--kompass-ink-soft)]"
                          }`}
                        >
                          <span>{l.native}</span>
                          <span className="text-[11px] uppercase tracking-wider">
                            {l.code}
                          </span>
                        </button>
                      );
                    })}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          <button
            type="button"
            onClick={() => goTo("contact")}
            data-testid="header-cta"
            className="hidden md:inline-flex btn-primary"
          >
            {t.nav.cta}
          </button>

          <button
            type="button"
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
            className="lg:hidden flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(31,107,122,0.15)] bg-white/70 text-[color:var(--kompass-primary)]"
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden border-t border-[rgba(31,107,122,0.08)] bg-white/90 backdrop-blur-xl"
            data-testid="mobile-menu"
          >
            <div className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-4">
              {navItems.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => goTo(n.id)}
                  data-testid={`mobile-nav-${n.id}`}
                  className="rounded-xl px-3 py-3 text-start text-base font-medium text-[color:var(--kompass-ink-soft)] hover:bg-[rgba(31,107,122,0.06)]"
                >
                  {n.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => goTo("contact")}
                data-testid="mobile-cta"
                className="btn-primary mt-2 justify-center"
              >
                {t.nav.cta}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
